import type { ReactNode } from 'react';
import type { ColDef, ColTypeDef } from '@ag-grid-community/core';
import type { GridColDef } from '../../ICSAgGrid';
import type {
    AppliedFilter,
    ColumnTypeMap,
    SearchableColumn,
    SearchOption,
} from './types';

let _filterId = 0;
export const nextId = (): string => String(++_filterId);

const DATE_COL_TYPES = new Set(['shortDate']);
const NUMBER_COL_TYPES = new Set(['numericColumn']);
const NUMBER_FILTERS = new Set(['agNumberColumnFilter', 'agFloatColumnFilter']);
const DATE_FILTERS = new Set(['agDateColumnFilter']);
const DATE_DATA_TYPES = new Set(['date', 'dateString']);

export function getFieldValue(data: unknown, field: string): unknown {
    return field.split('.').reduce<unknown>((obj, key) => {
        if (obj === null || obj === undefined) return undefined;
        return (obj as Record<string, unknown>)[key];
    }, data);
}

export function resolvedColType(
    col: ColDef,
    colTypes: ColumnTypeMap,
): ColTypeDef {
    const typeName = Array.isArray(col.type) ? col.type[0] : col.type;
    return (typeName ? colTypes[typeName] : undefined) ?? {};
}

function resolveFilter(col: ColDef, colType: ColTypeDef): string {
    if (typeof col.filter === 'string') return col.filter;
    if (typeof colType.filter === 'string') return colType.filter;
    return '';
}

export function resolveInputType(
    col: ColDef,
    colTypes: ColumnTypeMap,
): SearchableColumn['inputType'] {
    const colType = resolvedColType(col, colTypes);
    const filter = resolveFilter(col, colType);
    if (filter === 'agSetColumnFilter') return 'select';
    const typeName = Array.isArray(col.type) ? col.type[0] : col.type;
    const dataType =
        typeof col.cellDataType === 'string' ? col.cellDataType : '';
    if (
        DATE_COL_TYPES.has(typeName ?? '') ||
        DATE_FILTERS.has(filter) ||
        DATE_DATA_TYPES.has(dataType)
    )
        return 'date';
    if (
        NUMBER_COL_TYPES.has(typeName ?? '') ||
        NUMBER_FILTERS.has(filter) ||
        dataType === 'number'
    )
        return 'number';
    return 'text';
}

function extractStaticOptions(
    col: ColDef,
    colTypes: ColumnTypeMap,
): Pick<SearchableColumn, 'options' | 'dynamicOptions'> {
    const colType = resolvedColType(col, colTypes);
    const filter = resolveFilter(col, colType);
    if (filter !== 'agSetColumnFilter') return {};

    const refData = col.refData ?? colType.refData;
    if (refData) {
        return {
            options: Object.entries(refData).map(([value, label]) => ({
                value,
                label,
            })),
        };
    }

    const filterParams = (col.filterParams ?? colType.filterParams) as
        | { values?: unknown }
        | undefined;
    const values = filterParams?.values;
    if (Array.isArray(values)) {
        return {
            options: values
                .filter((v): v is string => typeof v === 'string')
                .map((v) => ({ value: v, label: v })),
        };
    }

    return { dynamicOptions: true };
}

function resolveOptionRenderer(
    col: ColDef,
    colType: ColTypeDef,
): ((value: string) => ReactNode) | undefined {
    // Skip cellRenderer when refData provides labels — the renderer likely needs full row data
    if (col.refData ?? colType.refData) return undefined;
    const renderer = (col.cellRenderer ?? colType.cellRenderer) as unknown;
    if (typeof renderer !== 'function') return undefined;
    return (value: string) =>
        (renderer as (params: { value: string }) => ReactNode)({ value });
}

export function extractLeafColumns<T>(
    cols: GridColDef<T>[],
    colTypes: ColumnTypeMap,
    groupName?: string,
): SearchableColumn[] {
    return cols.flatMap((col) => {
        if ('children' in col) {
            return extractLeafColumns(
                col.children as GridColDef<T>[],
                colTypes,
                col.headerName ?? groupName,
            );
        }
        const colDef = col as ColDef<T>;
        const { hide, initialHide, headerName, colId, field } = colDef;
        const displayName =
            headerName ??
            (field
                ? field.charAt(0).toUpperCase() + field.slice(1)
                : undefined);
        if ((hide ?? initialHide ?? !displayName) || colDef.filter === false)
            return [];
        const colType = resolvedColType(colDef, colTypes);
        const inputType = resolveInputType(colDef, colTypes);
        return [
            {
                colId: colId ?? field ?? displayName,
                field,
                headerName: displayName,
                inputType,
                groupName,
                ...extractStaticOptions(colDef, colTypes),
                ...(inputType === 'select'
                    ? { optionRenderer: resolveOptionRenderer(colDef, colType) }
                    : {}),
            },
        ];
    });
}

export function groupByCategory(
    columns: SearchableColumn[],
): Record<string, SearchableColumn[]> {
    return columns.reduce<Record<string, SearchableColumn[]>>((acc, col) => {
        (acc[col.groupName ?? ''] ??= []).push(col);
        return acc;
    }, {});
}

export const NUMBER_FILTER_OPTIONS: SearchOption[] = [
    { value: 'equals', label: 'Equal to' },
    { value: 'greaterThan', label: 'Greater than' },
    { value: 'greaterThanOrEqual', label: 'Greater than or equal' },
    { value: 'lessThan', label: 'Less than' },
    { value: 'lessThanOrEqual', label: 'Less than or equal' },
];

export const DATE_FILTER_OPTIONS: SearchOption[] = [
    { value: 'equals', label: 'On' },
    { value: 'lessThan', label: 'Before' },
    { value: 'lessThanOrEqual', label: 'On or before' },
    { value: 'greaterThan', label: 'After' },
    { value: 'greaterThanOrEqual', label: 'On or after' },
];

export function getPlaceholder(pendingColumn: SearchableColumn | null): string {
    if (pendingColumn?.inputType === 'select')
        return `Filter "${pendingColumn.headerName}" options`;
    if (pendingColumn?.inputType === 'date') return `e.g. 2026-04-14`;
    if (pendingColumn?.inputType === 'number') return `Enter value`;
    if (pendingColumn) return `Value for "${pendingColumn.headerName}"`;
    return 'Search';
}
